"use client";

import { useState, useEffect } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Users,
  UserCheck,
  CreditCard,
  BarChart3,
  Settings,
  User,
  Menu as MenuIcon,
  X,
  LogOut,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Badge,
  Box,
  Typography,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
  Tooltip,
} from "@mui/material";
import apiEndpoints from "../apiconfig";

// Layout configuration
const layoutConfig = {
  navigation: [
    {
      name: "Dashboard",
      href: "/dashboard",
      icon: LayoutDashboard,
      key: "dashboard",
    },
    { name: "Users", href: "/dashboard/users", icon: Users, key: "user" },
    {
      name: "Enquiries",
      href: "/dashboard/enquiries",
      icon: MessageSquare,
      key: "enquiries",
    },
    {
      name: "Approvals",
      href: "/dashboard/approvals",
      icon: UserCheck,
      key: "approvals",
    },

    {
      name: "Payments",
      href: "/dashboard/payments",
      icon: CreditCard,
      key: "payments",
    },
    {
      name: "Reports",
      href: "/dashboard/reports",
      icon: BarChart3,
      key: "reports",
    },
    {
      name: "Settings",
      href: "/dashboard/settings",
      icon: Settings,
      key: "settings",
    },
  ],
};

const DRAWER_WIDTH = 280;

export default function DashboardLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme(); // Global theme from App.js
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Permissions logic
  const userPermissions = JSON.parse(
    localStorage.getItem("permissions") || "{}",
  );
  const allowedNav = layoutConfig.navigation.filter((item) => {
    if (item.key === "enquiries" && userPermissions[item.key] === undefined)
      return true;
    return userPermissions[item.key] === 1;
  });

  // Pending count logic
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const fetchPendingCount = async () => {
      try {
        const response = await fetch(apiEndpoints.count);
        const data = await response.json();

        if (data.success && typeof data.data.not_verified_users === "number") {
          setPendingCount(data.data.not_verified_users);
        } else {
          setPendingCount(0);
        }
      } catch (err) {
        console.error("Error fetching pending count:", err);
      }
    };

    fetchPendingCount();
    const interval = setInterval(fetchPendingCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Snackbar logic
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    showSnackbar("Logout successful!", "success");
    setTimeout(() => {
      navigate("/", { replace: true });
    }, 1500);
  };

  const isCurrentPath = (href) => {
    if (href === "/dashboard") return location.pathname === "/dashboard";
    return location.pathname.startsWith(href);
  };

  // Shared Sidebar Content
  const SidebarContent = () => (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Sidebar Header */}
      <Box
        sx={{
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderBottom: "1px solid rgba(255,255,255,0.1)",
        }}
      >
        <Box
          component="img"
          src="/logo.png"
          alt="Logo"
          sx={{
            width: 140,
            height: "auto",
            filter: "brightness(0) invert(1)", // Make logo white for dark sidebar
          }}
        />
      </Box>

      {/* Navigation List */}
      <List sx={{ flex: 1, px: 2, py: 3 }}>
        {allowedNav.map((item) => {
          const Icon = item.icon;
          const current = isCurrentPath(item.href);

          return (
            <ListItem key={item.name} disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                component={Link}
                to={item.href}
                onClick={() => setMobileMenuOpen(false)}
                selected={current}
                sx={{
                  borderRadius: 2,
                  py: 1.5,
                  px: 2,
                  color: "#9ca3af", // gray-400
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,0.05)",
                    color: "#ffffff",
                    "& .lucide": { color: "#D4AF37" },
                  },
                  "&.Mui-selected": {
                    bgcolor: "#D4AF37 !important", // Premium Gold
                    color: "#000000 !important",
                    "& .lucide": { color: "#000000" },
                    "&:hover": {
                      bgcolor: "#b5952f !important",
                    },
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Icon
                    size={22}
                    className="lucide"
                    color={current ? "#000000" : "#9ca3af"}
                    style={{ transition: "color 0.2s" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{
                    fontSize: "0.95rem",
                    fontWeight: current ? 600 : 500,
                  }}
                />
                {item.key === "approvals" && pendingCount > 0 && (
                  <Box
                    sx={{
                      bgcolor: current ? "#000000" : "#D4AF37",
                      color: current ? "#D4AF37" : "#000000",
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      px: 1,
                      py: 0.25,
                      borderRadius: 4,
                    }}
                  >
                    {pendingCount}
                  </Box>
                )}
                {current && <ChevronRight size={16} />}
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Sidebar Footer */}
      <Box sx={{ p: 2, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.4)",
            display: "block",
            textAlign: "center",
          }}
        >
          © {new Date().getFullYear()} Matrimony Admin
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        bgcolor: "background.default",
      }}
    >
      {/* Desktop Sidebar */}
      <Box
        component="nav"
        sx={{
          width: { lg: DRAWER_WIDTH },
          flexShrink: { lg: 0 },
          display: { xs: "none", lg: "block" },
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", lg: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: "#1a1a1a", // Soft Black
              color: "#ffffff",
              borderRight: "none",
            },
          }}
          open
        >
          <SidebarContent />
        </Drawer>
      </Box>

      {/* Main Content Area */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          height: "100vh", // Fixed height for inner scroll
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: "background.paper",
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Toolbar sx={{ justifyContent: "space-between", minHeight: 70 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <IconButton
                color="inherit"
                edge="start"
                onClick={() => setMobileMenuOpen(true)}
                sx={{ display: { lg: "none" }, color: "text.primary" }}
              >
                <MenuIcon size={24} />
              </IconButton>

              {/* Page Title / Breadcrumb Placeholder */}
              <Box sx={{ display: { xs: "none", sm: "block" } }}>
                {/* <Typography variant="h6" color="text.primary">
                  {layoutConfig.navigation.find(item => isCurrentPath(item.href))?.name || "Dashboard"}
                </Typography> */}
              </Box>
            </Box>

            {/* Right Side Actions */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Tooltip title="Profile Settings">
                <IconButton
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    p: 0.5,
                    border: "2px solid transparent",
                    "&:hover": { border: "2px solid #D4AF37" },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: "secondary.main",
                      color: "secondary.contrastText",
                      fontWeight: 600,
                    }}
                  >
                    A
                  </Avatar>
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                onClick={() => setAnchorEl(null)}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible",
                    filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.15))",
                    mt: 1.5,
                    "&:before": {
                      content: '""',
                      display: "block",
                      position: "absolute",
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)",
                      zIndex: 0,
                    },
                  },
                }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              >
                <MenuItem onClick={() => navigate("/dashboard/settings")}>
                  <ListItemIcon>
                    <User size={18} />
                  </ListItemIcon>
                  Profile
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleLogout} sx={{ color: "error.main" }}>
                  <ListItemIcon>
                    <LogOut size={18} color="red" />
                  </ListItemIcon>
                  Sign Out
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          variant="temporary"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", lg: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: DRAWER_WIDTH,
              bgcolor: "#1a1a1a",
              color: "#ffffff",
            },
          }}
        >
          <Box sx={{ position: "absolute", top: 16, right: 16, zIndex: 1 }}>
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              sx={{ color: "grey.500" }}
            >
              <X />
            </IconButton>
          </Box>
          <SidebarContent />
        </Drawer>

        {/* Page Content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: { xs: 2, md: 4 },
            width: "100%",
            overflowY: "auto", // Allow scrolling within the content area
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Box>
      </Box>

      {/* Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%", borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
